import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Plus, Eye, CheckCircle2, AlertCircle, AlertTriangle, Calendar,
  ChevronDown, ChevronRight, Layers,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState } from "@/components/ui";
import {
  expiryBand, bandStyle, daysToExpiry, fmtDate,
  CATEGORY_DISPLAY, CATEGORY_ORDER, COLLAPSED_BY_DEFAULT,
} from "./licenceHelpers";

// =============================================================================
// LicencesScreen — /licences
// Employee's own licence register. Cards grouped by category with yellow/red
// expiry banding (≤60d / overdue). Entry points to /licences/new and
// /licences/inspector. T-004 Commit C.
// =============================================================================

interface LicenceRow {
  licence_id:         string;
  licence_type_id:    string;
  licence_number:     string | null;
  issue_date:         string | null;
  expiry_date:        string | null;
  state_issued:       string | null;
  verified_at:        string | null;
  front_photo_url:    string | null;
  back_photo_url:     string | null;
  notes:              string | null;
  external_url:       string | null;
  licence_type: {
    code:                  string;
    display_name:          string;
    category:              string;
    issuing_body:          string | null;
    requires_verification: boolean;
    external_system:       string | null;
  } | null;
}

export default function LicencesScreen() {
  const user = useRequireAuth();
  // Inductions and other noisy categories collapse by default — click to expand.
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());

  const { data: licences, isLoading, error } = useQuery({
    queryKey: ["my_licences", user?.employee_id],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select(`
          licence_id, licence_type_id, licence_number, issue_date, expiry_date,
          state_issued, verified_at, front_photo_url, back_photo_url, notes,
          external_url,
          licence_type:licence_types (
            code, display_name, category, issuing_body,
            requires_verification, external_system
          )
        `)
        .eq("employee_id", user!.employee_id!)
        .eq("is_active", true)
        .order("expiry_date", { ascending: true, nullsFirst: false });

      if (error) throw error;
      return (data ?? []) as unknown as LicenceRow[];
    },
  });

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------
  if (!user?.employee_id) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-text-muted text-sm">
            Your login isn't linked to an employee record yet. Ask a director
            to complete setup.
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 flex items-start gap-3">
            <AlertTriangle size={18} className="text-danger flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">Can't load your licences</div>
              <div className="text-sm text-text-muted">
                {error instanceof Error ? error.message : "Unknown error"}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Shape data
  // ---------------------------------------------------------------------------
  const list = licences ?? [];

  // Group by category
  const byCategory = new Map<string, LicenceRow[]>();
  for (const l of list) {
    const cat = l.licence_type?.category ?? "other";
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(l);
  }

  // Summary
  const overdueCount  = list.filter(l => expiryBand(l.expiry_date) === "overdue").length;
  const warningCount  = list.filter(l => expiryBand(l.expiry_date) === "warning").length;
  const pendingVerify = list.filter(
    l => l.licence_type?.requires_verification && !l.verified_at
  ).length;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="p-6 max-w-4xl mx-auto pb-24">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">My licences</h1>
          <p className="text-sm text-text-muted mt-1">
            Licences, inductions, and tickets held under your name.
            Expiring soon is anything within 60 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/licences/inspector">
            <Button variant="ghost" icon={Eye}>Inspector view</Button>
          </Link>
          <Link to="/licences/bulk">
            <Button variant="secondary" icon={Layers}>Bulk add</Button>
          </Link>
          <Link to="/licences/new">
            <Button variant="primary" icon={Plus}>Add licence</Button>
          </Link>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <SummaryCard label="Total"          value={list.length}    icon={CheckCircle2} tone="ok" />
        <SummaryCard label="Expiring soon"  value={warningCount}   icon={Calendar}
          tone={warningCount > 0 ? "warning" : "ok"} hint="within 60 days" />
        <SummaryCard label="Overdue"        value={overdueCount}   icon={AlertCircle}
          tone={overdueCount > 0 ? "danger" : "ok"} />
      </div>

      {pendingVerify > 0 && (
        <Card className="mb-4 border-warn/30">
          <div className="p-4 flex items-center gap-3 text-sm">
            <AlertTriangle size={16} className="text-warn flex-shrink-0" />
            <span>
              {pendingVerify} licence{pendingVerify === 1 ? "" : "s"} awaiting
              director verification. You'll see them below with a
              "Pending verification" badge until a director signs them off.
            </span>
          </div>
        </Card>
      )}

      {list.length === 0 ? (
        <Card>
          <div className="p-6">
            <EmptyState
              title="No licences yet"
              description="Add your first licence to get started. Photos, expiry dates, and verification all live in one place."
            />
            <div className="mt-4 flex justify-center">
              <Link to="/licences/new">
                <Button variant="primary" icon={Plus}>Add your first licence</Button>
              </Link>
            </div>
          </div>
        </Card>
      ) : (
        CATEGORY_ORDER
          .filter(cat => byCategory.has(cat))
          .map(cat => {
            const items         = byCategory.get(cat)!;
            const collapsedDflt = COLLAPSED_BY_DEFAULT.has(cat);
            const isExpanded    = collapsedDflt ? expandedCats.has(cat) : true;
            const toggle = () => {
              if (!collapsedDflt) return;
              setExpandedCats(prev => {
                const next = new Set(prev);
                next.has(cat) ? next.delete(cat) : next.add(cat);
                return next;
              });
            };
            return (
              <div key={cat} className="mb-6">
                <h2
                  className={`text-[11px] font-semibold text-text-muted uppercase tracking-wider2 mb-2 px-1 flex items-center gap-1.5 ${
                    collapsedDflt ? "cursor-pointer hover:text-text select-none" : ""
                  }`}
                  onClick={toggle}
                  role={collapsedDflt ? "button" : undefined}
                  aria-expanded={collapsedDflt ? isExpanded : undefined}
                >
                  {collapsedDflt && (
                    isExpanded
                      ? <ChevronDown size={12} className="opacity-70"/>
                      : <ChevronRight size={12} className="opacity-70"/>
                  )}
                  {CATEGORY_DISPLAY[cat] ?? cat}
                  <span className="font-normal normal-case tracking-normal text-text-muted/80">
                    ({items.length})
                  </span>
                  {collapsedDflt && !isExpanded && (
                    <span className="font-normal normal-case tracking-normal text-text-dim text-[10px] ml-auto italic">
                      tap to expand
                    </span>
                  )}
                </h2>
                {isExpanded && (
                  <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                    {items.map(l => <LicenceCard key={l.licence_id} licence={l} />)}
                  </div>
                )}
              </div>
            );
          })
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface SummaryCardProps {
  label: string;
  value: number | string;
  icon:  typeof CheckCircle2;
  tone:  "ok" | "warning" | "danger";
  hint?: string;
}

function SummaryCard({ label, value, icon: Icon, tone, hint }: SummaryCardProps) {
  const toneClass = tone === "danger" ? "text-danger"
                  : tone === "warning" ? "text-warn"
                  : "text-ok";
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider2 text-text-muted">
          <Icon size={12} className={toneClass} />
          <span>{label}</span>
        </div>
        <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
        {hint && <div className="text-[11px] text-text-muted mt-0.5">{hint}</div>}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function LicenceCard({ licence }: { licence: LicenceRow }) {
  const band        = expiryBand(licence.expiry_date);
  const style       = bandStyle(band);
  const days        = daysToExpiry(licence.expiry_date);
  const needsVerify = licence.licence_type?.requires_verification && !licence.verified_at;

  return (
    <Card className={style.cardBg}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm leading-tight">
              {licence.licence_type?.display_name ?? "Unknown licence type"}
            </div>
            <div className="text-[11px] text-text-muted font-mono mt-0.5">
              {licence.licence_number ?? "(no number)"}
              {licence.state_issued ? ` · ${licence.state_issued}` : ""}
            </div>
          </div>
          {needsVerify && <Pill label="Pending verification" tone="warning" small />}
        </div>
        <div className="flex items-center justify-between mt-3 text-[12px]">
          <div className="text-text-muted">
            {licence.expiry_date ? (
              <>
                <span>Expires {fmtDate(licence.expiry_date)}</span>
                {days !== null && band === "warning" && (
                  <span className={`ml-2 font-semibold ${style.text}`}>
                    · {days}d left
                  </span>
                )}
                {days !== null && band === "overdue" && (
                  <span className={`ml-2 font-semibold ${style.text}`}>
                    · {Math.abs(days)}d overdue
                  </span>
                )}
              </>
            ) : (
              <span>No expiry</span>
            )}
          </div>
          {licence.external_url && (
            <a
              href={licence.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline"
            >
              View in {licence.licence_type?.external_system ?? "portal"} ↗
            </a>
          )}
        </div>
      </div>
    </Card>
  );
}
